import {
   AfterViewInit,
   ChangeDetectorRef,
   Component,
   ElementRef,
   EventEmitter,
   Input,
   NgZone,
   OnChanges,
   OnDestroy,
   OnInit,
   Output,
   QueryList,
   SimpleChanges,
   ViewChildren,
   ViewEncapsulation,
} from '@angular/core';
import { KtdGridComponent } from '@katoid/angular-grid-layout';
import { LcsBreakpoints, LcsScreensizeBreakpointsService } from '@lcs/shared/breakpoints';
import { QueueAgentStatus, QueueCallerStatus, Tiles, TileTypes, TileViewModes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { extensionsRoute } from 'src/app/app-routing.module';
import { HangUpCallsPrivileges } from 'src/app/pages/active-calls/models/hang-up-calls.privileges';
import { compactLayout, mapLayoutToTargetColumns } from 'src/app/pages/dashboard/models/dashboard-layout-utils';
import { DashboardTile } from 'src/app/pages/dashboard/models/dashboard-tile.model';
import { DashboardGridLayoutItem } from 'src/app/pages/dashboard/views/dashboard-view.component';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import {
   QueueHistoryRegisterSessionStorageService,
   QueueHistoryToggleState,
} from 'src/app/pages/queue-history-register/services/queue-history-register-session-storage.service';
import { ThemeService } from 'src/app/pages/site-layout/themes/theme.service';
import { addSpacesToTypeName } from 'src/app/pipes/split-enum-strings/add-spaces.pipe';
import {
   QueueMembersGridItem,
   QueueMembersGridLayoutSpec,
   QueueMembersMemberGridItem,
   QueueMembersQueueAgentGridItem,
   QueueMembersQueueCallGridItem,
   QueueMembersQueueGridItem,
   QueueMemberVisualState,
} from '../models/queue-history-register-queue-members-view.model';
import {
   LoggedInQueueCallDisplay,
   LoggedInQueueDisplay,
   LoggedInQueueDisplayAgent,
   LoggedInQueueMemberDisplay,
   LoggedInQueueMemberQueue,
} from '../models/queue-history-register-queue-members.model';
import { QueueHistoryRegisterListItem } from '../models/queue-history-register-results.model';

@Component({
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-queue-history-register-queue-members-view',
   templateUrl: './queue-history-register-queue-members-view.component.html',
   standalone: false,
})
export class QueueHistoryRegisterQueueMembersViewComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
   @Input() displayMode: 'agents' | 'queues' | 'queue-calls' = 'agents';

   @Input() groupMembersByQueue = false;

   @Input() wrapSwimlanes = false;

   @Input() hangupActiveCallsPrivileges: HangUpCallsPrivileges | null = null;

   @Input() isActive = true;

   @Input() isQueueFilterSortable = false;

   @Input() gridRefreshVersion = 0;

   @Input() loggedInQueueMembers: LoggedInQueueMemberDisplay[] = [];

   @Input() loggedInQueueGroups: LoggedInQueueDisplay[] = [];

   @Input() queueCallGroups: LoggedInQueueCallDisplay[] = [];

   @Input() set queueExtensions(value: ExtensionOption[]) {
      this._queueExtensions = value ?? [];

      if (this.displayMode === 'queue-calls') {
         this.tileConfigCache.clear();
         this.refreshResponsiveGridLayouts();
      }
   }

   get queueExtensions(): ExtensionOption[] {
      return this._queueExtensions;
   }

   @Input() selectedQueueExtensionIds: number[] = [];

   @Output() groupMembersByQueueChange = new EventEmitter<boolean>();
   @Output() wrapSwimlanesChange = new EventEmitter<boolean>();
   @Output() onOpenCallDetails = new EventEmitter<QueueHistoryRegisterListItem>();

   showLoggedOutAgents = false;

   showEmptyQueues = false;

   includeActiveCalls = false;

   @ViewChildren(KtdGridComponent) private gridComponents!: QueryList<KtdGridComponent>;

   readonly queueMemberGridGap = 12;

   readonly queueMemberGridRowHeight = 4;

   readonly groupedQueueMemberGridRowHeight = 4;

   private readonly memberTileStaticHeight = 84;

   private readonly memberQueuePillGap = 6.4;

   private readonly groupedQueueAgentTileHeight = 84;

   private readonly groupedQueueBannerHeight = 52;

   private readonly queueTileStaticHeight = 84;

   private readonly queueCallsTileStaticHeight = 84;

   private readonly queueCallPillHeight = 38;

   private readonly queueCallEmptyStateHeight = 28;

   private readonly memberQueuePillVerticalPadding = 9;

   private readonly memberQueuePillLineHeight = 18;

   queueMemberGridColumns = 6;

   readonly trackGridItem = (_index: number, item: QueueMembersGridItem): number => {
      return item.id;
   };

   readonly trackGridLayoutItem = (_index: number, item: DashboardGridLayoutItem): number => {
      return item.id;
   };

   readonly trackQueue = (_index: number, queue: LoggedInQueueMemberQueue): number => {
      return queue.queueExtensionId;
   };

   readonly trackQueueAgent = (_index: number, agent: LoggedInQueueDisplayAgent): string => {
      if (agent.agentExtensionId != null) {
         return `agent-${agent.agentExtensionId}`;
      }

      return this.getObjectKey(agent, 'queue-card-agent');
   };

   readonly trackQueueCall = (_index: number, queueCall: QueueHistoryRegisterListItem): number => {
      return queueCall.queueHistoryId;
   };

   private readonly tileConfigCache = new Map<string, DashboardTile>();

   private readonly localIdCache = new Map<string, number>();

   private objectKeys = new WeakMap<object, string>();

   private readonly visualStateAccentColorCache = new Map<QueueMemberVisualState, string>();

   private queueCardAccentColor: string | null = null;

   private readonly visualStateAccentColorVariableNames: Record<QueueMemberVisualState, string> = {
      'on-the-phone': '--queue-member-state-on-the-phone-accent',
      'dnd': '--queue-member-state-dnd-accent',
      'ringing': '--queue-member-state-ringing-accent',
      'wrap-up': '--queue-member-state-wrap-up-accent',
      'available': '--queue-member-state-available-accent',
      'unknown': '--queue-member-state-unknown-accent',
   };

   private readonly visualStateAccentColorFallbacks: Record<QueueMemberVisualState, string> = {
      'on-the-phone': '#248232',
      'dnd': '#eb343c',
      'ringing': '#fcb716',
      'wrap-up': '#13537f',
      'available': '#90a4ae',
      'unknown': '#78909c',
   };

   private nextLocalId = -1;

   private nextObjectKey = 1;

   private liveUpdateInterval: ReturnType<typeof setInterval> | null = null;

   private resizeObserver: ResizeObserver | null = null;

   private scheduledRefreshFrameId: number | null = null;

   private scheduledGridResizeFrameId: number | null = null;

   private lastObservedHostWidth = 0;

   private lastViewportWidth = 0;

   private lastDevicePixelRatio = 1;

   private currentTime = new Date();

   private readonly destroyed$ = new Subject<void>();

   memberGridItems: QueueMembersMemberGridItem[] = [];

   memberGridLayout: DashboardGridLayoutItem[] = [];

   queueGridItems: QueueMembersQueueGridItem[] = [];

   queueGridLayout: DashboardGridLayoutItem[] = [];

   queueCallGridItems: QueueMembersQueueCallGridItem[] = [];

   queueCallGridLayout: DashboardGridLayoutItem[] = [];

   groupedGridItems: QueueMembersGridItem[] = [];

   groupedGridLayout: DashboardGridLayoutItem[] = [];

   private memberGridLayoutById = new Map<number, DashboardGridLayoutItem>();

   private queueGridLayoutById = new Map<number, DashboardGridLayoutItem>();

   private queueCallGridLayoutById = new Map<number, DashboardGridLayoutItem>();

   private groupedGridLayoutById = new Map<number, DashboardGridLayoutItem>();

   private _queueExtensions: ExtensionOption[] = [];

   constructor(
      private screensizeBreakpointsService: LcsScreensizeBreakpointsService,
      private elementRef: ElementRef<HTMLElement>,
      private ngZone: NgZone,
      private changeDetectorRef: ChangeDetectorRef,
      private themeService: ThemeService,
      private sessionStorageService: QueueHistoryRegisterSessionStorageService
   ) {}

   ngOnInit(): void {
      this.restoreToggleState();

      this.liveUpdateInterval = setInterval(() => {
         this.ngZone.run(() => {
            this.currentTime = new Date();
            this.tileConfigCache.clear();
            this.visualStateAccentColorCache.clear();
            this.changeDetectorRef.detectChanges();
         });
      }, 1000);

      this.refreshResponsiveGridLayouts();

      this.screensizeBreakpointsService
         .screensizeChange()
         .pipe(takeUntil(this.destroyed$))
         .subscribe(() => {
            this.scheduleResponsiveGridRefresh();
         });

      this.themeService.currentTheme$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.refreshThemeDependentTileState();
      });
   }

   ngAfterViewInit(): void {
      this.gridComponents.changes.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.scheduleGridResize();
      });

      this.startResizeObserver();
      this.startViewportResizeListeners();
      this.scheduleResponsiveGridRefresh();
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (
         changes['loggedInQueueMembers'] ||
         changes['loggedInQueueGroups'] ||
         changes['queueCallGroups'] ||
         changes['displayMode'] ||
         changes['selectedQueueExtensionIds']
      ) {
         this.tileConfigCache.clear();
         this.visualStateAccentColorCache.clear();
         this.refreshResponsiveGridLayouts();
      }

      if (changes['isActive']?.currentValue || changes['gridRefreshVersion']) {
         this.scheduleResponsiveGridRefresh();
      }
   }

   ngOnDestroy(): void {
      if (this.liveUpdateInterval) {
         clearInterval(this.liveUpdateInterval);
      }

      if (this.scheduledRefreshFrameId != null) {
         cancelAnimationFrame(this.scheduledRefreshFrameId);
      }

      if (this.scheduledGridResizeFrameId != null) {
         cancelAnimationFrame(this.scheduledGridResizeFrameId);
      }

      this.stopViewportResizeListeners();
      this.resizeObserver?.disconnect();

      this.destroyed$.next();
      this.destroyed$.complete();
   }

   onGroupMembersByQueueChange(groupMembersByQueue: boolean): void {
      this.groupMembersByQueueChange.emit(groupMembersByQueue);
   }

   onShowLoggedOutAgentsChange(showLoggedOutAgents: boolean): void {
      this.showLoggedOutAgents = showLoggedOutAgents;
      this.saveToggleState(
         this.displayMode === 'queues'
            ? { queuesShowLoggedOutAgents: showLoggedOutAgents }
            : { agentsShowLoggedOutAgents: showLoggedOutAgents }
      );
      this.tileConfigCache.clear();
      this.refreshResponsiveGridLayouts();
   }

   onShowEmptyQueuesChange(showEmptyQueues: boolean): void {
      this.showEmptyQueues = showEmptyQueues;
      this.saveToggleState({ queueCallsShowEmptyQueues: showEmptyQueues });
      this.tileConfigCache.clear();
      this.refreshResponsiveGridLayouts();
   }

   onIncludeActiveCallsChange(includeActiveCalls: boolean): void {
      this.includeActiveCalls = includeActiveCalls;
      this.saveToggleState({ queueCallsIncludeActiveCalls: includeActiveCalls });
      this.tileConfigCache.clear();
      this.refreshResponsiveGridLayouts();
   }

   onWrapSwimlanesChange(wrapSwimlanes: boolean): void {
      this.wrapSwimlanes = wrapSwimlanes;
      this.wrapSwimlanesChange.emit(wrapSwimlanes);
   }

   canOpenCallDetails(queueCall: QueueHistoryRegisterListItem): boolean {
      return !!queueCall.callDetailId;
   }

   get isSwimlaneWrapAvailable(): boolean {
      return this.displayMode === 'queues' || this.displayMode === 'queue-calls';
   }

   get isWrappedSwimlaneLayout(): boolean {
      return this.isSwimlaneWrapAvailable && this.wrapSwimlanes;
   }

   onQueueCallClick(queueCall: QueueHistoryRegisterListItem): void {
      if (!this.canOpenCallDetails(queueCall)) {
         return;
      }

      this.onOpenCallDetails.emit(queueCall);
   }

   canOpenQueueAgentCallDetails(agent: LoggedInQueueDisplayAgent): boolean {
      const visualState = this.getQueueAgentVisualState(agent);

      return !!agent.activeQueueCall?.callDetailId && (visualState === 'on-the-phone' || visualState === 'ringing');
   }

   onQueueAgentCallDetailClick(event: MouseEvent, agent: LoggedInQueueDisplayAgent): void {
      if (!this.canOpenQueueAgentCallDetails(agent) || !agent.activeQueueCall) {
         return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.onOpenCallDetails.emit(agent.activeQueueCall);
   }

   get hasVisibleQueueMembers(): boolean {
      if (this.displayMode === 'queue-calls') {
         return this.queueCallGridItems.length > 0;
      }

      if (this.displayMode === 'queues') {
         return this.queueGridItems.length > 0;
      }

      return this.groupMembersByQueue ? this.groupedGridItems.length > 0 : this.memberGridItems.length > 0;
   }

   getQueueExtensionLink(id: number): string {
      return `/${extensionsRoute}/${id}`;
   }

   getQueueDisplay(queueNumber: number | null, queueName: string | null): string {
      if (queueNumber != null && queueName) {
         return `${queueNumber} - ${queueName}`;
      }

      if (queueName) {
         return queueName;
      }

      if (queueNumber != null) {
         return `${queueNumber}`;
      }

      return 'Unnamed Queue';
   }

   getAgentDisplay(agentName: string | null, agentNumber: string | null): string {
      if (agentName) {
         return agentName;
      }

      if (agentNumber) {
         return agentNumber;
      }

      return 'Unnamed Agent';
   }

   getQueueAgentCountLabel(agentCount: number): string {
      return `${agentCount} agent${agentCount === 1 ? '' : 's'}`;
   }

   getQueueCallCountLabel(callCount: number): string {
      return `${callCount} call${callCount === 1 ? '' : 's'}`;
   }

   getQueueCallsForQueue(queueExtensionId: number): QueueHistoryRegisterListItem[] {
      const queueCallGroup = this.queueCallGroups.find((group) => {
         return group.queueExtensionId === queueExtensionId;
      });

      return queueCallGroup?.activeCalls ?? [];
   }

   getConnectedQueueCallCount(queueCalls: QueueHistoryRegisterListItem[]): number {
      return queueCalls.filter((queueCall) => this.isConnectedQueueCall(queueCall)).length;
   }

   getWaitingQueueCallCount(queueCalls: QueueHistoryRegisterListItem[]): number {
      return queueCalls.length - this.getConnectedQueueCallCount(queueCalls);
   }

   getQueueCallStatLabel(queueCalls: QueueHistoryRegisterListItem[]): string {
      return `c: ${this.getConnectedQueueCallCount(queueCalls)} / w: ${this.getWaitingQueueCallCount(queueCalls)}`;
   }

   getQueueCallStatTooltip(queueCalls: QueueHistoryRegisterListItem[]): string {
      return `Connected Calls: ${this.getConnectedQueueCallCount(
         queueCalls
      )}\nWaiting Calls: ${this.getWaitingQueueCallCount(queueCalls)}`;
   }

   getLoggedInQueueAgentCount(queueExtensionId: number): number {
      const queueGroup = this.loggedInQueueGroups.find((loggedInQueueGroup) => {
         return loggedInQueueGroup.queueExtensionId === queueExtensionId;
      });
      if (!queueGroup) {
         return 0;
      }

      return queueGroup.agents.filter((agent) => {
         return !this.isUnavailableStatus(agent.agentStatus);
      }).length;
   }

   getPossibleQueueAgentCount(queueExtensionId: number): number {
      const queueGroup = this.loggedInQueueGroups.find((loggedInQueueGroup) => {
         return loggedInQueueGroup.queueExtensionId === queueExtensionId;
      });

      return queueGroup?.agents.length ?? 0;
   }

   getLoggedInQueueAgentCountLabel(queueExtensionId: number): string {
      const loggedInAgentCount = this.getLoggedInQueueAgentCount(queueExtensionId);
      return `${loggedInAgentCount} agent${loggedInAgentCount === 1 ? '' : 's'}`;
   }

   getQueueAgentStatLabel(queueExtensionId: number): string {
      return `l: ${this.getLoggedInQueueAgentCount(queueExtensionId)} / p: ${this.getPossibleQueueAgentCount(
         queueExtensionId
      )}`;
   }

   getQueueAgentStatTooltip(queueExtensionId: number): string {
      return `Logged In Agents: ${this.getLoggedInQueueAgentCount(
         queueExtensionId
      )}\nPossible Agents: ${this.getPossibleQueueAgentCount(queueExtensionId)}`;
   }

   getQueueCallNumberDisplay(queueCall: QueueHistoryRegisterListItem): string {
      return queueCall.callerIdNumber?.trim() || 'Unknown';
   }

   getQueueCallStatusLabel(queueCall: QueueHistoryRegisterListItem): string {
      switch (queueCall.callerStatus) {
         case QueueCallerStatus.DialingAnAgent:
            return 'Dialing';
         case QueueCallerStatus.CallbackRequested:
         case QueueCallerStatus.InitiateQueueCallback:
            return 'Callback';
         default:
            return queueCall.callerStatus ? addSpacesToTypeName(queueCall.callerStatus) : 'Unknown';
      }
   }

   getQueueCallVisualStateClass(queueCall: QueueHistoryRegisterListItem): string | null {
      const visualState = this.getQueueCallVisualState(queueCall);
      return visualState ? this.getVisualStateClass(visualState) : null;
   }

   getQueueCallDurationStartDate(queueCall: QueueHistoryRegisterListItem): Date {
      return this.isConnectedQueueCall(queueCall) && queueCall.answeredDateTime
         ? queueCall.answeredDateTime
         : queueCall.enqueuedDateTime;
   }

   getQueueCallDurationEndDate(queueCall: QueueHistoryRegisterListItem): Date | null {
      return this.isConnectedQueueCall(queueCall) ? null : queueCall.answeredDateTime;
   }

   getQueueCallDurationTooltipLabel(queueCall: QueueHistoryRegisterListItem): string {
      return this.isConnectedQueueCall(queueCall) ? 'Talk Time' : 'Wait Time';
   }

   getQueueAgentsTabStatusLabel(agent: LoggedInQueueDisplayAgent): string {
      if (this.getQueueAgentVisualState(agent) === 'on-the-phone') {
         return 'Connected';
      }

      return this.getStatusLabel(agent.agentStatus, agent.endWrapUpTime, this.isQueueAgentOnThePhone(agent));
   }

   getQueueAgentSecondaryQueueLabel(agent: LoggedInQueueDisplayAgent): string | null {
      if (agent.activeCallQueue) {
         return `${this.getQueueDisplay(agent.activeCallQueue.queueNumber, agent.activeCallQueue.queueName)}`;
      }

      if (agent.activeRingingQueue) {
         return `${this.getQueueDisplay(agent.activeRingingQueue.queueNumber, agent.activeRingingQueue.queueName)}`;
      }

      return null;
   }

   getMemberTileConfig(item: QueueMembersMemberGridItem): DashboardTile {
      return this.getTileConfig(
         item.cacheKey,
         this.getAgentDisplay(item.member.agentName, item.member.agentNumber),
         this.getVisualStateAccentColor(this.getMemberVisualState(item.member)),
         this.memberGridLayoutById.get(item.id)
      );
   }

   getQueueTileConfig(item: QueueMembersQueueGridItem): DashboardTile {
      return this.getTileConfig(
         item.cacheKey,
         this.getQueueDisplay(item.queue.queueNumber, item.queue.queueName),
         this.getQueueCardAccentColor(),
         this.queueGridLayoutById.get(item.id)
      );
   }

   getQueueCallsTileConfig(item: QueueMembersQueueCallGridItem): DashboardTile {
      return this.getTileConfig(
         item.cacheKey,
         this.getQueueDisplay(item.queue.queueNumber, item.queue.queueName),
         this.getQueueCardAccentColor(),
         this.queueCallGridLayoutById.get(item.id)
      );
   }

   getQueueAgentTileConfig(item: QueueMembersQueueAgentGridItem): DashboardTile {
      return this.getTileConfig(
         item.cacheKey,
         this.getAgentDisplay(item.agent.agentName, item.agent.agentNumber),
         this.getVisualStateAccentColor(this.getQueueAgentVisualState(item.agent)),
         this.groupedGridLayoutById.get(item.id)
      );
   }

   isQueueCallActive(queue: LoggedInQueueMemberQueue): boolean {
      return queue.isOnThePhone || queue.isRinging;
   }

   isQueuePhoneIconVisible(queue: LoggedInQueueMemberQueue): boolean {
      return queue.isOnThePhone || queue.isRinging;
   }

   getQueuePhoneIconTooltip(queue: LoggedInQueueMemberQueue): string {
      return queue.isRinging ? 'Ringing in this queue' : 'On phone in this queue';
   }

   isMemberOnThePhone(member: LoggedInQueueMemberDisplay): boolean {
      return member.queues.some((queue) => queue.isOnThePhone);
   }

   getMemberVisualState(member: LoggedInQueueMemberDisplay): QueueMemberVisualState {
      return this.resolveVisualState(member.agentStatus, member.endWrapUpTime, this.isMemberOnThePhone(member));
   }

   getQueueAgentVisualState(agent: LoggedInQueueDisplayAgent): QueueMemberVisualState {
      if (this.isQueueAgentOnThePhone(agent)) {
         return 'on-the-phone';
      }

      if (this.isQueueAgentRinging(agent)) {
         return 'ringing';
      }

      return this.resolveVisualState(agent.agentStatus, agent.endWrapUpTime, this.isQueueAgentOnThePhone(agent));
   }

   isQueueAgentAvailable(agent: LoggedInQueueDisplayAgent): boolean {
      return this.getQueueAgentVisualState(agent) === 'available';
   }

   getQueueAgentStatusLabel(agent: LoggedInQueueDisplayAgent): string {
      switch (this.getQueueAgentVisualState(agent)) {
         case 'on-the-phone':
            return 'On The Phone';
         case 'ringing':
            return 'Ringing';
         default:
            return this.getStatusLabel(agent.agentStatus, agent.endWrapUpTime, agent.isOnThePhone);
      }
   }

   getQueueAgentActivityTooltip(agent: LoggedInQueueDisplayAgent): string {
      if (agent.activeCallQueue && !agent.isOnThePhone) {
         return `On phone in ${this.getQueueDisplay(
            agent.activeCallQueue.queueNumber,
            agent.activeCallQueue.queueName
         )}`;
      }

      if (agent.activeRingingQueue && !agent.isRinging) {
         return `Ringing in ${this.getQueueDisplay(
            agent.activeRingingQueue.queueNumber,
            agent.activeRingingQueue.queueName
         )}`;
      }

      if (this.isQueueAgentRinging(agent)) {
         return 'Ringing in this queue';
      }

      return 'On phone in this queue';
   }

   isQueueAgentActive(agent: LoggedInQueueDisplayAgent): boolean {
      return this.isQueueAgentOnThePhone(agent) || this.isQueueAgentRinging(agent);
   }

   isQueueAgentPhoneIconVisible(agent: LoggedInQueueDisplayAgent): boolean {
      return agent.isOnThePhone || agent.isRinging;
   }

   getQueueAgentPhoneIconTooltip(agent: LoggedInQueueDisplayAgent): string {
      return agent.isRinging ? 'Ringing in this queue' : 'On phone in this queue';
   }

   private isQueueAgentOnThePhone(agent: LoggedInQueueDisplayAgent): boolean {
      return agent.isOnThePhone || !!agent.activeCallQueue;
   }

   private isQueueAgentRinging(agent: LoggedInQueueDisplayAgent): boolean {
      return agent.isRinging || !!agent.activeRingingQueue;
   }

   getVisualStateClass(visualState: QueueMemberVisualState): string {
      return `queue-member-visual-state-${visualState}`;
   }

   getStatusLabel(
      status: string | null,
      endWrapUpTime: Date | null,
      isOnThePhone = false,
      fallbackLabel = 'Logged In'
   ): string {
      if (this.isUnavailableStatus(status)) {
         return 'Unavailable';
      }

      const visualState = this.resolveVisualState(status, endWrapUpTime, isOnThePhone);

      switch (visualState) {
         case 'on-the-phone':
            return 'On The Phone';
         case 'dnd':
            return 'DND';
         case 'ringing':
            return 'Ringing';
         case 'wrap-up':
            return 'Wrap Up';
         case 'available':
            return 'Available';
         default:
            return status ? addSpacesToTypeName(status) : fallbackLabel;
      }
   }

   private resolveVisualState(
      status: string | null,
      endWrapUpTime: Date | null,
      isOnThePhone: boolean
   ): QueueMemberVisualState {
      if (isOnThePhone || status === QueueAgentStatus.OnThePhone) {
         return 'on-the-phone';
      }

      if (status === QueueAgentStatus.DND) {
         return 'dnd';
      }

      if (this.isUnavailableStatus(status)) {
         return 'dnd';
      }

      if (status === QueueAgentStatus.Ringing) {
         return 'ringing';
      }

      if (status === QueueAgentStatus.Available && endWrapUpTime && endWrapUpTime > this.currentTime) {
         return 'wrap-up';
      }

      if (status === QueueAgentStatus.Available) {
         return 'available';
      }

      return 'unknown';
   }

   private isUnavailableStatus(status: string | null): boolean {
      return status?.trim().toLocaleLowerCase() === 'unavailable';
   }

   private getVisualStateAccentColor(visualState: QueueMemberVisualState): string {
      const cachedColor = this.visualStateAccentColorCache.get(visualState);
      if (cachedColor) {
         return cachedColor;
      }

      const cssVariableName = this.visualStateAccentColorVariableNames[visualState];
      const fallbackColor = this.visualStateAccentColorFallbacks[visualState];
      const accentColor = this.getCssCustomPropertyValue(cssVariableName, fallbackColor);

      this.visualStateAccentColorCache.set(visualState, accentColor);
      return accentColor;
   }

   private refreshThemeDependentTileState(): void {
      this.visualStateAccentColorCache.clear();
      this.tileConfigCache.clear();
      this.queueCardAccentColor = null;

      if (typeof requestAnimationFrame !== 'function') {
         this.changeDetectorRef.detectChanges();
         return;
      }

      this.ngZone.runOutsideAngular(() => {
         requestAnimationFrame(() => {
            this.ngZone.run(() => {
               this.changeDetectorRef.detectChanges();
            });
         });
      });
   }

   private getCssCustomPropertyValue(variableName: string, fallbackColor: string): string {
      if (typeof window === 'undefined') {
         return fallbackColor;
      }

      const cssColorValue = getComputedStyle(this.elementRef.nativeElement).getPropertyValue(variableName).trim();
      return cssColorValue || fallbackColor;
   }

   private getQueueCardAccentColor(): string {
      if (this.queueCardAccentColor) {
         return this.queueCardAccentColor;
      }

      this.queueCardAccentColor = this.getCssCustomPropertyValue('--queue-member-state-available-accent', '#90a4ae');
      return this.queueCardAccentColor;
   }

   private getTileConfig(
      cacheKey: string,
      name: string,
      color: string,
      layoutItem: DashboardGridLayoutItem | undefined
   ): DashboardTile {
      const existingConfig = this.tileConfigCache.get(cacheKey);
      const localTileId = this.getLocalId(cacheKey);
      const width = layoutItem?.w ?? 1;
      const height = layoutItem?.h ?? 1;
      const x = layoutItem?.x ?? 0;
      const y = layoutItem?.y ?? 0;

      if (existingConfig) {
         existingConfig.dashboardTileLayoutConfiguration.name = name;
         existingConfig.dashboardTileLayoutConfiguration.color = color;
         existingConfig.dashboardTileLayoutConfiguration.width = width;
         existingConfig.dashboardTileLayoutConfiguration.height = height;
         existingConfig.dashboardTileLayoutConfiguration.x = x;
         existingConfig.dashboardTileLayoutConfiguration.y = y;
         return existingConfig;
      }

      const tileConfig: DashboardTile = {
         dashboardTileId: localTileId,
         totalMillisecondsProcessed: 0,
         totalTimesProcessed: 0,
         viewMode: TileViewModes.Small,
         dashboardTileParameters: [],
         tile: {
            tileId: Tiles.QueueMembers,
            name: 'Queue Members',
            description: 'Queue Members',
            typeId: TileTypes.Table,
            canEditRefreshTime: false,
            tileViewModeBound: null,
            designerDescription: 'Queue Members',
            isCustom: true,
         },
         dashboardTileLayoutConfiguration: {
            dashboardTileLayoutConfigurationId: localTileId,
            name,
            minWidth: null,
            minHeight: null,
            maxWidth: null,
            maxHeight: null,
            width,
            height,
            x,
            y,
            color,
            refreshTimeSeconds: null,
         },
      };

      this.tileConfigCache.set(cacheKey, tileConfig);
      return tileConfig;
   }

   private refreshGridLayouts(): void {
      this.memberGridItems = this.buildMemberGridItems();
      this.memberGridLayout = this.buildMemberGridLayout(this.memberGridItems);
      this.memberGridLayoutById = this.indexLayoutById(this.memberGridLayout);

      this.queueGridItems = this.buildQueueGridItems();
      this.queueGridLayout = this.buildQueueGridLayout(this.queueGridItems);
      this.queueGridLayoutById = this.indexLayoutById(this.queueGridLayout);

      this.queueCallGridItems = this.buildQueueCallGridItems();
      this.queueCallGridLayout = this.buildQueueCallGridLayout(this.queueCallGridItems);
      this.queueCallGridLayoutById = this.indexLayoutById(this.queueCallGridLayout);

      this.groupedGridItems = this.buildGroupedGridItems();
      this.groupedGridLayout = this.buildGroupedGridLayout(this.groupedGridItems);
      this.groupedGridLayoutById = this.indexLayoutById(this.groupedGridLayout);
   }

   private refreshResponsiveGridLayouts(): void {
      this.syncGridColumnsFromViewport();
      this.refreshGridLayouts();
      this.scheduleGridResize();
   }

   private scheduleResponsiveGridRefresh(): void {
      if (this.scheduledRefreshFrameId != null) {
         cancelAnimationFrame(this.scheduledRefreshFrameId);
      }

      this.scheduledRefreshFrameId = requestAnimationFrame(() => {
         this.scheduledRefreshFrameId = null;
         this.refreshResponsiveGridLayouts();
      });
   }

   private startResizeObserver(): void {
      if (typeof ResizeObserver === 'undefined') {
         return;
      }

      this.resizeObserver = new ResizeObserver((entries) => {
         const observedWidth = Math.round(
            entries[0]?.contentRect.width ?? this.elementRef.nativeElement.getBoundingClientRect().width
         );

         if (observedWidth === this.lastObservedHostWidth) {
            return;
         }

         this.lastObservedHostWidth = observedWidth;
         this.ngZone.run(() => {
            this.scheduleResponsiveGridRefresh();
         });
      });

      this.resizeObserver.observe(this.elementRef.nativeElement);
   }

   private startViewportResizeListeners(): void {
      if (typeof window === 'undefined') {
         return;
      }

      this.lastViewportWidth = Math.round(window.visualViewport?.width ?? window.innerWidth);
      this.lastDevicePixelRatio = window.devicePixelRatio || 1;

      window.addEventListener('resize', this.handleViewportResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.handleViewportResize, { passive: true });
   }

   private stopViewportResizeListeners(): void {
      if (typeof window === 'undefined') {
         return;
      }

      window.removeEventListener('resize', this.handleViewportResize);
      window.visualViewport?.removeEventListener('resize', this.handleViewportResize);
   }

   private readonly handleViewportResize = (): void => {
      if (typeof window === 'undefined') {
         return;
      }

      const viewportWidth = Math.round(window.visualViewport?.width ?? window.innerWidth);
      const devicePixelRatio = window.devicePixelRatio || 1;

      if (viewportWidth === this.lastViewportWidth && devicePixelRatio === this.lastDevicePixelRatio) {
         return;
      }

      this.lastViewportWidth = viewportWidth;
      this.lastDevicePixelRatio = devicePixelRatio;
      this.ngZone.run(() => {
         this.scheduleResponsiveGridRefresh();
      });
   };

   private syncGridColumnsFromViewport(): void {
      if (this.matchesBreakpoint(LcsBreakpoints.Small)) {
         this.queueMemberGridColumns = 1;
         return;
      }

      if (this.matchesBreakpoint(LcsBreakpoints.Medium)) {
         this.queueMemberGridColumns = 2;
         return;
      }

      this.queueMemberGridColumns = this.baseGridColumns;
   }

   private scheduleGridResize(): void {
      if (!this.isActive) {
         return;
      }

      if (this.scheduledGridResizeFrameId != null) {
         cancelAnimationFrame(this.scheduledGridResizeFrameId);
      }

      this.scheduledGridResizeFrameId = requestAnimationFrame(() => {
         this.scheduledGridResizeFrameId = requestAnimationFrame(() => {
            this.scheduledGridResizeFrameId = null;
            this.rebindGridLayouts();
            this.gridComponents?.forEach((gridComponent) => {
               gridComponent.resize();
            });
         });
      });
   }

   private rebindGridLayouts(): void {
      this.memberGridLayout = [...this.memberGridLayout];
      this.queueGridLayout = [...this.queueGridLayout];
      this.queueCallGridLayout = [...this.queueCallGridLayout];
      this.groupedGridLayout = [...this.groupedGridLayout];
   }

   private matchesBreakpoint(breakpoint: LcsBreakpoints): boolean {
      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
         return window.matchMedia(breakpoint).matches;
      }

      return this.screensizeBreakpointsService.breakpoint() === breakpoint;
   }

   private get baseGridColumns(): number {
      return this.displayMode === 'queue-calls' ? 7 : 6;
   }

   private buildMemberGridItems(): QueueMembersMemberGridItem[] {
      const visibleMembers = this.showLoggedOutAgents
         ? this.loggedInQueueMembers.filter((member) => member.queues.length === 0) // logged OUT
         : this.loggedInQueueMembers.filter((member) => member.queues.length > 0); // logged IN

      return visibleMembers.map((member) => {
         const cacheKey = this.getMemberCacheKey(member);
         return {
            kind: 'member',
            cacheKey,
            member,
            id: this.getLocalId(cacheKey),
         };
      });
   }

   private buildQueueGridItems(): QueueMembersQueueGridItem[] {
      return this.getVisibleQueueAgentGroups().map((queue) => {
         const cacheKey = this.getQueueCacheKey(queue);
         return {
            kind: 'queue',
            cacheKey,
            queue,
            id: this.getLocalId(cacheKey),
         };
      });
   }

   private buildQueueCallGridItems(): QueueMembersQueueCallGridItem[] {
      return this.getVisibleQueueCallGroups().map((queue) => {
         const cacheKey = this.getQueueCallsCacheKey(queue);
         return {
            kind: 'queue-calls',
            cacheKey,
            queue,
            id: this.getLocalId(cacheKey),
         };
      });
   }

   private buildGroupedGridItems(): QueueMembersGridItem[] {
      const gridItems: QueueMembersGridItem[] = [];

      this.getVisibleQueueAgentGroups().forEach((visibleQueue) => {
         gridItems.push({
            kind: 'queue-banner',
            queue: visibleQueue,
            id: this.getLocalId(`queue-banner-${visibleQueue.queueExtensionId}`),
         });

         visibleQueue.agents.forEach((agent) => {
            const cacheKey = this.getQueueAgentCacheKey(visibleQueue, agent);
            gridItems.push({
               kind: 'queue-agent',
               cacheKey,
               queue: visibleQueue,
               agent,
               id: this.getLocalId(cacheKey),
            });
         });
      });

      return gridItems;
   }

   private getVisibleQueueAgentGroups(): LoggedInQueueDisplay[] {
      const queueGroupsById = new Map<number, LoggedInQueueDisplay>(
         this.loggedInQueueGroups.map((queueGroup) => {
            return [queueGroup.queueExtensionId, queueGroup];
         })
      );

      const visibleQueueGroups = this.selectedQueueExtensionIds.reduce((queueGroups, queueExtensionId) => {
         const queueGroup = queueGroupsById.get(queueExtensionId);
         const queueExtension = this.queueExtensions.find((item) => item.extensionId === queueExtensionId);
         const baseQueue: LoggedInQueueDisplay = queueGroup ?? {
            queueExtensionId,
            queueName: this.getQueueDisplayName(queueExtension?.shortName, queueExtension?.name),
            queueNumber: queueExtension?.number ?? null,
            agents: [],
         };

         const visibleAgents = this.showLoggedOutAgents
            ? baseQueue.agents.filter((agent) => this.isUnavailableStatus(agent.agentStatus))
            : baseQueue.agents.filter((agent) => !this.isUnavailableStatus(agent.agentStatus));

         if (!visibleAgents.length) {
            return queueGroups;
         }

         queueGroups.push(
            visibleAgents === baseQueue.agents
               ? baseQueue
               : {
                    ...baseQueue,
                    agents: visibleAgents,
                 }
         );

         return queueGroups;
      }, [] as LoggedInQueueDisplay[]);

      return visibleQueueGroups;
   }

   private buildMemberGridLayout(gridItems: QueueMembersMemberGridItem[]): DashboardGridLayoutItem[] {
      const baseLayout = this.buildSequentialBaseLayout(
         gridItems.map((gridItem) => {
            return {
               id: gridItem.id,
               dashboardTileId: gridItem.id,
               w: 1,
               h: this.getMemberGridHeight(gridItem.member),
               minW: 1,
               minH: 1,
            };
         })
      );

      return this.mapLocalLayout(baseLayout);
   }

   private buildQueueGridLayout(gridItems: QueueMembersQueueGridItem[]): DashboardGridLayoutItem[] {
      const baseLayout = this.buildSequentialBaseLayout(
         gridItems.map((gridItem) => {
            return {
               id: gridItem.id,
               dashboardTileId: gridItem.id,
               w: 1,
               h: this.getQueueGridHeight(gridItem.queue),
               minW: 1,
               minH: 1,
            };
         })
      );

      return this.mapLocalLayout(baseLayout);
   }

   private buildQueueCallGridLayout(gridItems: QueueMembersQueueCallGridItem[]): DashboardGridLayoutItem[] {
      const baseLayout = this.buildSequentialBaseLayout(
         gridItems.map((gridItem) => {
            return {
               id: gridItem.id,
               dashboardTileId: gridItem.id,
               w: 1,
               h: this.getQueueCallsGridHeight(gridItem.queue),
               minW: 1,
               minH: 1,
            };
         })
      );

      return this.mapLocalLayout(baseLayout);
   }

   private buildGroupedGridLayout(gridItems: QueueMembersGridItem[]): DashboardGridLayoutItem[] {
      const baseLayout = this.buildSequentialBaseLayout(
         gridItems.map((gridItem) => {
            if (gridItem.kind === 'queue-banner') {
               return {
                  id: gridItem.id,
                  dashboardTileId: gridItem.id,
                  w: this.baseGridColumns,
                  h: this.getGroupedQueueBannerGridHeight(),
                  minW: 1,
                  minH: 1,
               };
            }

            return {
               id: gridItem.id,
               dashboardTileId: gridItem.id,
               w: 1,
               h: this.getGroupedQueueAgentGridHeight(),
               minW: 1,
               minH: 1,
            };
         })
      );

      return this.mapLocalLayout(baseLayout);
   }

   private buildSequentialBaseLayout(layoutSpecs: QueueMembersGridLayoutSpec[]): DashboardGridLayoutItem[] {
      let currentColumn = 0;
      let currentRow = 0;
      let currentRowHeight = 0;

      return compactLayout(
         layoutSpecs.map((layoutSpec) => {
            const width = Math.min(layoutSpec.w, this.baseGridColumns);

            if (currentColumn + width > this.baseGridColumns) {
               currentRow += currentRowHeight;
               currentColumn = 0;
               currentRowHeight = 0;
            }

            const layoutItem: DashboardGridLayoutItem = {
               id: layoutSpec.id,
               dashboardTileId: layoutSpec.dashboardTileId,
               w: width,
               h: layoutSpec.h,
               x: currentColumn,
               y: currentRow,
               minW: layoutSpec.minW,
               minH: layoutSpec.minH,
               maxW: layoutSpec.maxW,
               maxH: layoutSpec.maxH,
            };

            currentColumn += width;
            currentRowHeight = Math.max(currentRowHeight, layoutSpec.h);

            return layoutItem;
         }),
         'vertical',
         this.baseGridColumns
      );
   }

   private mapLocalLayout(layout: DashboardGridLayoutItem[]): DashboardGridLayoutItem[] {
      if (!layout.length) {
         return [];
      }

      if (this.queueMemberGridColumns === this.baseGridColumns) {
         return compactLayout(layout, 'vertical', this.baseGridColumns);
      }

      return compactLayout(
         mapLayoutToTargetColumns(this.baseGridColumns, this.queueMemberGridColumns, layout),
         'vertical',
         this.queueMemberGridColumns
      );
   }

   private indexLayoutById(layout: DashboardGridLayoutItem[]): Map<number, DashboardGridLayoutItem> {
      return new Map(
         layout.map((layoutItem) => {
            return [layoutItem.id, layoutItem];
         })
      );
   }

   private getMemberGridHeight(member: LoggedInQueueMemberDisplay): number {
      const queueSectionHeight = member.queues.reduce((totalHeight, queue, index) => {
         const queuePillHeight = this.memberQueuePillVerticalPadding + this.memberQueuePillLineHeight;

         return totalHeight + queuePillHeight + (index > 0 ? this.memberQueuePillGap : 0);
      }, 0);
      const targetHeight = this.memberTileStaticHeight + queueSectionHeight;

      return this.getGridUnitsForPixelHeight(targetHeight, this.queueMemberGridRowHeight);
   }

   private getQueueGridHeight(queue: LoggedInQueueDisplay): number {
      const agentSectionHeight = queue.agents.reduce((totalHeight, _agent, index) => {
         const agentPillHeight = this.memberQueuePillVerticalPadding + this.memberQueuePillLineHeight;

         return totalHeight + agentPillHeight + (index > 0 ? this.memberQueuePillGap : 0);
      }, 0);
      const targetHeight = this.queueTileStaticHeight + agentSectionHeight;

      return this.getGridUnitsForPixelHeight(targetHeight, this.queueMemberGridRowHeight);
   }

   private getQueueCallsGridHeight(queue: LoggedInQueueCallDisplay): number {
      const callListHeight = queue.activeCalls.length
         ? queue.activeCalls.reduce((totalHeight, _queueCall, index) => {
              return totalHeight + this.queueCallPillHeight + (index > 0 ? this.memberQueuePillGap : 0);
           }, 0)
         : this.queueCallEmptyStateHeight;
      const targetHeight = this.queueCallsTileStaticHeight + callListHeight;

      return this.getGridUnitsForPixelHeight(targetHeight, this.queueMemberGridRowHeight);
   }

   private getVisibleQueueCallGroups(): LoggedInQueueCallDisplay[] {
      const queueCallGroupsById = new Map<number, LoggedInQueueCallDisplay>(
         this.queueCallGroups.map((queueCallGroup) => {
            return [queueCallGroup.queueExtensionId, queueCallGroup];
         })
      );

      const visibleQueueExtensionIds = this.showEmptyQueues
         ? this.selectedQueueExtensionIds
         : this.selectedQueueExtensionIds.filter((queueExtensionId) => {
              return this.getLoggedInQueueAgentCount(queueExtensionId) > 0;
           });

      const visibleQueueGroups = visibleQueueExtensionIds.map((queueExtensionId) => {
         const queueCallGroup = queueCallGroupsById.get(queueExtensionId);
         if (queueCallGroup) {
            return {
               ...queueCallGroup,
               activeCalls: this.filterVisibleQueueCalls(queueCallGroup.activeCalls),
            };
         }

         const queueExtension = this.queueExtensions.find((item) => item.extensionId === queueExtensionId);
         return {
            queueExtensionId,
            queueName: this.getQueueDisplayName(queueExtension?.shortName, queueExtension?.name),
            queueNumber: queueExtension?.number ?? null,
            activeCalls: [],
         };
      });

      return visibleQueueGroups;
   }

   private filterVisibleQueueCalls(queueCalls: QueueHistoryRegisterListItem[]): QueueHistoryRegisterListItem[] {
      if (this.includeActiveCalls) {
         return queueCalls;
      }

      return queueCalls.filter((queueCall) => {
         return !this.isConnectedQueueCall(queueCall);
      });
   }

   private isConnectedQueueCall(queueCall: QueueHistoryRegisterListItem): boolean {
      if (queueCall.callerStatus == null) {
         return false;
      }

      if (queueCall.callerStatus === QueueCallerStatus.Connected) {
         return true;
      }

      return String(queueCall.callerStatus).toLocaleLowerCase() === QueueCallerStatus.Connected.toLocaleLowerCase();
   }

   private getQueueCallVisualState(queueCall: QueueHistoryRegisterListItem): QueueMemberVisualState | null {
      if (this.isConnectedQueueCall(queueCall)) {
         return 'on-the-phone';
      }

      switch (queueCall.callerStatus) {
         case QueueCallerStatus.DialingAnAgent:
            return 'ringing';
         case QueueCallerStatus.CallbackRequested:
         case QueueCallerStatus.InitiateQueueCallback:
            return 'wrap-up';
         default:
            return null;
      }
   }

   private restoreToggleState(): void {
      const toggleState = this.getStoredToggleState();

      if (this.displayMode === 'agents') {
         this.showLoggedOutAgents = toggleState.agentsShowLoggedOutAgents;
         return;
      }

      if (this.displayMode === 'queues') {
         this.showLoggedOutAgents = toggleState.queuesShowLoggedOutAgents;
         return;
      }

      if (this.displayMode === 'queue-calls') {
         this.includeActiveCalls = toggleState.queueCallsIncludeActiveCalls;
         this.showEmptyQueues = toggleState.queueCallsShowEmptyQueues;
      }
   }

   private saveToggleState(toggleStatePatch: Partial<QueueHistoryToggleState>): void {
      const currentToggleState = this.getStoredToggleState();

      this.sessionStorageService.setQueueHistoryToggleState({
         ...currentToggleState,
         ...toggleStatePatch,
      });
   }

   private getStoredToggleState(): QueueHistoryToggleState {
      return {
         ...this.getDefaultToggleState(),
         ...(this.sessionStorageService.getQueueHistoryToggleState() ?? {}),
      };
   }

   private getDefaultToggleState(): QueueHistoryToggleState {
      return {
         groupMembersByQueue: false,
         agentsShowLoggedOutAgents: false,
         queuesShowLoggedOutAgents: false,
         queuesWrapSwimlanes: true,
         queueCallsIncludeActiveCalls: false,
         queueCallsShowEmptyQueues: false,
         queueCallsWrapSwimlanes: true,
      };
   }

   private getGroupedQueueAgentGridHeight(): number {
      return this.getGridUnitsForPixelHeight(this.groupedQueueAgentTileHeight, this.groupedQueueMemberGridRowHeight);
   }

   private getGroupedQueueBannerGridHeight(): number {
      return this.getGridUnitsForPixelHeight(this.groupedQueueBannerHeight, this.groupedQueueMemberGridRowHeight);
   }

   private getGridUnitsForPixelHeight(targetHeight: number, rowHeight: number): number {
      return Math.max(1, Math.ceil((targetHeight + this.queueMemberGridGap) / (rowHeight + this.queueMemberGridGap)));
   }

   private getMemberCacheKey(member: LoggedInQueueMemberDisplay): string {
      if (member.agentExtensionId != null) {
         return `agent-${member.agentExtensionId}`;
      }
      return this.getObjectKey(member, 'member');
   }

   private getQueueCacheKey(queue: LoggedInQueueDisplay): string {
      if (queue.queueExtensionId != null) {
         return `queue-${queue.queueExtensionId}`;
      }

      return this.getObjectKey(queue, 'queue');
   }

   private getQueueCallsCacheKey(queue: LoggedInQueueCallDisplay): string {
      if (queue.queueExtensionId != null) {
         return `queue-calls-${queue.queueExtensionId}`;
      }

      return this.getObjectKey(queue, 'queue-calls');
   }

   private getQueueDisplayName(shortName: string | null | undefined, name: string | null | undefined): string | null {
      return shortName?.trim() || name?.trim() || null;
   }

   private getQueueAgentCacheKey(queue: LoggedInQueueDisplay, agent: LoggedInQueueDisplayAgent): string {
      const agentKey =
         agent.agentExtensionId != null ? `agent-${agent.agentExtensionId}` : this.getObjectKey(agent, 'queue-agent');

      return `queue-${queue.queueExtensionId}-${agentKey}`;
   }

   private getLocalId(cacheKey: string): number {
      const existingId = this.localIdCache.get(cacheKey);
      if (existingId != null) {
         return existingId;
      }

      const nextId = this.nextLocalId--;
      this.localIdCache.set(cacheKey, nextId);
      return nextId;
   }

   private getObjectKey(target: object, prefix: string): string {
      const existingKey = this.objectKeys.get(target);
      if (existingKey) {
         return existingKey;
      }

      const nextKey = `${prefix}-${this.nextObjectKey++}`;
      this.objectKeys.set(target, nextKey);
      return nextKey;
   }
}
